import { APP_SETTINGS } from "../../shared/app_settings";
import { CHANNELS } from "../../shared/channels";
import { NoteEntry } from "../../shared/types";
import { renderMarkdown } from "../../shared/markdown";
import { call, listen } from "../bridge";
import { byId, flashToast, renderList } from "../dom";
import { locale, t } from "../i18n";
import { onViewHidden, onViewShown } from "../navigation";
import { debounce, matchesSearch } from "../utils";
import { applyStickyColor, buildStickyNote, userNotes } from "./sticky_note";
import { MAX_NOTE_PINS, dropNotePin, isNotePinned, loadNotePins, onNotePinsChanged, pruneNotePins, renameNotePin, toggleNotePin } from "../note_pins";
import { fillScratchpad, setupScratchpad } from "./scratchpad";
import { printMarkdown } from "../print_sheet";
import { onSharedChange, readSharedAll, sharedHead } from "../shares";

// sticky note grid plus a markdown editor that autosaves as you type

let notes: NoteEntry[] = [];
let peeked: Array<{ name: string; notes: NoteEntry[] }> = [];
let openName = "";
// a note someone shares with you opens in view mode and never saves
let readOnly = false;
let mode: "write" | "preview" = "write";

const nameInput = () => byId<HTMLInputElement>("notes-name");
const bodyInput = () => byId<HTMLTextAreaElement>("notes-body");
const preview = () => byId("notes-preview");

function renderNotes(): void {
  const search = byId<HTMLInputElement>("notes-search").value;
  const visible = userNotes(notes).filter((note) => matchesSearch(search, note.name, note.content));
  renderList(byId("notes-list"), visible, (note) => buildStickyNote(note, openEditor, true));
  renderPeeks(search);
}

function renderPeeks(search: string): void {
  const box = byId("notes-peek");
  box.hidden = peeked.length === 0;
  box.replaceChildren(...peeked.map((person) => {
    const section = document.createElement("div");
    section.className = "stack";
    const head = sharedHead(person.name, "notes");
    const grid = document.createElement("ul");
    grid.className = "sticky-grid";
    const visible = userNotes(person.notes).filter((note) => matchesSearch(search, note.name, note.content));
    renderList(grid, visible, (note) => buildStickyNote(note, openPeek));
    section.append(head, grid);
    return section;
  }));
}

async function loadPeeks(): Promise<void> {
  const people = await readSharedAll<NoteEntry[]>("notes", []);
  peeked = people.map((person) => ({ name: person.name, notes: person.data }));
}

async function reload(): Promise<void> {
  const [mine] = await Promise.all([call<NoteEntry[]>(CHANNELS.notesList), loadPeeks(), loadNotePins()]);
  notes = mine;
  pruneNotePins(notes.map((note) => note.name));
  fillScratchpad(notes);
  renderNotes();
}

function setEditing(editing: boolean): void {
  byId("notes-browser").hidden = editing;
  byId("notes-editor").hidden = !editing;
}

function paintMode(): void {
  const writing = mode === "write";
  bodyInput().hidden = !writing;
  preview().hidden = writing;
  byId("notes-mode-write").classList.toggle("active", writing);
  byId("notes-mode-preview").classList.toggle("active", !writing);
  if (!writing) preview().innerHTML = renderMarkdown(bodyInput().value);
}

// the pin toggle in the editor bar says what it will do in words, and shows whether this note is pinned
function paintPin(): void {
  const key = byId("notes-pin");
  const on = openName !== "" && isNotePinned(openName);
  key.setAttribute("aria-pressed", String(on));
  key.setAttribute("aria-label", t(on ? "pinned to home, press to unpin" : "pin to home"));
  byId("notes-pin-text").textContent = t(on ? "pinned" : "pin");
}

async function togglePin(): Promise<void> {
  if (readOnly) return;
  // a note not saved yet has no name to pin, so it is saved first
  if (!openName) await save();
  if (!toggleNotePin(openName)) flashToast(t("{count} pins is the limit, unpin one first", { count: MAX_NOTE_PINS }));
  paintPin();
}

function setReadOnly(value: boolean): void {
  readOnly = value;
  byId("notes-pin").hidden = value;
  nameInput().readOnly = value;
  bodyInput().readOnly = value;
  byId("notes-mode-write").hidden = value;
  byId("notes-delete").hidden = value;
}

function openPeek(note: NoteEntry): void {
  openEditor(note);
  setReadOnly(true);
  mode = "preview";
  paintMode();
}

function openEditor(note: NoteEntry | null): void {
  setReadOnly(false);
  openName = note?.name ?? "";
  nameInput().value = note?.name ?? t("sticky {date}", { date: new Date().toLocaleDateString(locale()) });
  bodyInput().value = note?.content ?? "";
  applyStickyColor(byId("notes-editor"), nameInput().value);
  byId("notes-status").textContent = "";
  paintPin();
  mode = note?.content ? "preview" : "write";
  paintMode();
  setEditing(true);
  if (mode === "write") bodyInput().focus();
}

async function save(): Promise<void> {
  if (readOnly) return;
  const name = nameInput().value.trim() || t("untitled");
  const saved = await call<NoteEntry>(CHANNELS.notesSave, name, bodyInput().value);
  if (openName && openName !== saved.name) {
    await call(CHANNELS.notesRemove, openName);
    renameNotePin(openName, saved.name);
  }
  openName = saved.name;
  paintPin();
  byId("notes-status").textContent = t("saved {time}", { time: new Date().toLocaleTimeString(locale()) });
  if (mode === "preview") preview().innerHTML = renderMarkdown(bodyInput().value);
}

const autosave = debounce(save, APP_SETTINGS.noteAutosaveMs);

function hasContent(): boolean {
  return openName !== "" || bodyInput().value.trim() !== "";
}

async function closeEditor(): Promise<void> {
  if (!byId("notes-editor").hidden && hasContent()) await save();
  setEditing(false);
  await reload();
}

async function deleteNote(): Promise<void> {
  if (readOnly) return;
  if (openName && confirm(t("delete {name}?", { name: openName }))) {
    await call(CHANNELS.notesRemove, openName);
    dropNotePin(openName);
  }
  openName = "";
  setEditing(false);
  await reload();
}

export async function setupNotesView(): Promise<void> {
  byId("notes-new").addEventListener("click", () => openEditor(null));
  byId("notes-back").addEventListener("click", closeEditor);
  byId("notes-delete").addEventListener("click", deleteNote);
  byId("notes-pin").addEventListener("click", () => void togglePin());
  onNotePinsChanged(() => {
    if (byId("notes-editor").hidden) renderNotes();
    else paintPin();
  });
  byId("notes-search").addEventListener("input", renderNotes);
  byId("notes-mode-write").addEventListener("click", () => {
    if (readOnly) return;
    mode = "write";
    paintMode();
    bodyInput().focus();
  });
  byId("notes-mode-preview").addEventListener("click", () => {
    mode = "preview";
    paintMode();
  });
  byId("notes-print").addEventListener("click", () => {
    printMarkdown(nameInput().value.trim() || t("note"), bodyInput().value);
  });
  setupScratchpad();
  nameInput().addEventListener("input", autosave);
  bodyInput().addEventListener("input", () => {
    autosave();
    if (mode === "preview") preview().innerHTML = renderMarkdown(bodyInput().value);
  });

  listen(CHANNELS.notesUpdated, () => {
    if (byId("notes-editor").hidden) void reload();
  });
  onSharedChange("notes", () => {
    if (byId("notes-editor").hidden) void reload();
  });
  onViewShown("notes", async (noteName) => {
    await reload();
    const target = notes.find((note) => note.name === noteName);
    if (target) openEditor(target);
  });
  onViewHidden("notes", () => {
    if (!byId("notes-editor").hidden && hasContent()) save();
  });
}
